import { useEffect, useId, useMemo, useRef, useState } from 'react';
import Tooltip from '../Tooltip';
import { PopoverShell } from '../PopoverShell/PopoverShell';
import { useNotes } from './useNotes';
import NotesPopoverBody from './NotesPopoverBody';
import styles from './NotesControl.module.css';

function formatStatus({ loading, saving, error, readOnly }) {
    if (readOnly) return 'Read-only';
    if (loading) return 'Loading…';
    if (saving) return 'Saving…';
    if (error) return 'Not saved';
    return 'Saved';
}

/**
 * NotesControl — a small topbar control for per-item free-text notes.
 *
 * - In edit mode, it loads/saves via `useNotes` (dedicated /annotation/notes endpoint).
 * - In readOnly mode, it renders the provided `notes` value without any API calls.
 */
export default function NotesControl({
    folderId,
    itemId,
    config = {},
    readOnly = false,
    mode = 'edit', // 'edit' | 'readOnly'
    notes: readOnlyNotes = '',
}) {
    const enabled = Boolean(config?.enabled ?? true);
    const label = (config?.label || 'Notes').trim() || 'Notes';
    const placeholder = config?.placeholder || 'Add notes…';
    const instructions = config?.instructions || '';
    const maxLength = Number.isFinite(config?.maxLength) ? config.maxLength : 5000;

    const [open, setOpen] = useState(false);
    const buttonRef = useRef(null);
    const [anchorRect, setAnchorRect] = useState(null);
    const reactId = useId();
    const titleId = `notes-popover-title-${reactId}`;
    const instructionsId = `notes-popover-desc-${reactId}`;

    const isReadOnly = readOnly || mode === 'readOnly';

    const notesState = useNotes({
        folderId,
        itemId,
        enabled: enabled && !isReadOnly,
        maxLength,
    });

    const value = isReadOnly
        ? (typeof readOnlyNotes === 'string' ? readOnlyNotes : '')
        : notesState.notes;

    const hasNotes = isReadOnly
        ? Boolean(value && value.trim().length > 0)
        : notesState.hasNotes;

    const statusText = useMemo(() => formatStatus({
        loading: notesState.loading,
        saving: notesState.saving,
        error: notesState.error,
        readOnly: isReadOnly,
    }), [isReadOnly, notesState.error, notesState.loading, notesState.saving]);

    useEffect(() => {
        if (!open) return;
        const rect = buttonRef.current?.getBoundingClientRect?.();
        if (rect) setAnchorRect(rect);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const onResize = () => {
            const rect = buttonRef.current?.getBoundingClientRect?.();
            if (rect) setAnchorRect(rect);
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onResize, true);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onResize, true);
        };
    }, [open]);

    if (!enabled) return null;

    const btn = (
        <button
            ref={buttonRef}
            type="button"
            className={styles.btn}
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-haspopup="dialog"
            title={label}
        >
            <span aria-hidden="true">📝</span>
            <span>{label}</span>
            {hasNotes ? <span className={styles.dot} aria-label="Notes present" /> : null}
        </button>
    );

    return (
        <div className={styles.wrap}>
            <Tooltip text={isReadOnly ? `${label} (read-only)` : label} position="bottom">
                {btn}
            </Tooltip>

            <PopoverShell
                open={open}
                strategy="fixed"
                anchorRect={anchorRect}
                placement="bottom-end"
                offset={8}
                pinned={false}
                onClose={() => { setOpen(false); if (!isReadOnly) notesState.flush(); }}
                titleId={titleId}
                bodyClassName=""
            >
                <NotesPopoverBody
                    label={label}
                    instructions={instructions}
                    value={value}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    statusText={statusText}
                    error={isReadOnly ? null : notesState.error}
                    isReadOnly={isReadOnly}
                    showCounter
                    onChange={(e) => notesState.setNotes(e.target.value)}
                    onBlur={() => notesState.flush()}
                    titleId={titleId}
                    instructionsId={instructionsId}
                />
            </PopoverShell>
        </div>
    );
}

